import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { usePagination } from "../../../../hooks/usePagination";
import { useTenant } from "../../../../hooks/useTenant";
import { toast } from "react-toastify";
import { ConfirmDialog } from "../../../../components/Common";
import ActivityService from "../../../../services/activity_service";

/**
 * useActivities
 * hook de lógica para o módulo de Atividades (Activities).
 * Utiliza o ActivityService para persistência.
 */
export const useActivities = () => {
    const { idTenant, user, hasNeoMethodology, planId } = useTenant();
    const [isSaving, setIsSaving] = useState(false);
    const hasSeedRun = useRef(false);

    const paginationOptions = useMemo(() => ({
        pageSize: 50,
        orderField: "order",
        orderDirection: "asc"
    }), []);

    // Paginação das Atividades
    const { data: activities, loading, error, refresh } = usePagination(
        `tenants/${idTenant}/activities`,
        idTenant,
        paginationOptions
    );

    /**
     * Auto-Seed: Cria as atividades padrão APENAS UMA VEZ se o tenant não tiver nenhuma.
     * Segue o mesmo padrão do useRoles.
     */
    useEffect(() => {
        if (hasSeedRun.current) return;
        if (loading || error || !idTenant || isSaving) return;

        const hasAccess = hasNeoMethodology === true || planId === "neo";

        if (activities.length > 0) {
            hasSeedRun.current = true;
            return;
        }

        if (!hasAccess) {
            return;
        }

        hasSeedRun.current = true;

        const seedDefaultActivities = async () => {
            try {
                const count = await ActivityService.seedDefaults(idTenant, user);
                if (count > 0) {
                    toast.success(`${count} atividades padrão configuradas!`);
                    refresh();
                }
            } catch (err) {
                console.error("[AutoSeed] Erro ao criar atividades padrão:", err);
                toast.error("Erro ao configurar atividades padrão.");
                hasSeedRun.current = false;
            }
        };

        seedDefaultActivities();
    }, [loading, error, idTenant, activities.length, isSaving, refresh, user, hasNeoMethodology, planId]);

    /**
     * Salvar uma Atividade
     */
    const saveActivity = useCallback(async (formData) => {
        if (!idTenant) return;
        setIsSaving(true);

        try {
            await ActivityService.save(formData, idTenant, user, activities);
            toast.success(formData.id ? "Atividade atualizada!" : "Nova atividade cadastrada!");
            refresh();
            return true;
        } catch (err) {
            console.error("Erro ao salvar atividade:", err);
            toast.error("Erro ao salvar atividade.");
            return false;
        } finally {
            setIsSaving(false);
        }
    }, [idTenant, user, activities, refresh]);

    /**
     * Excluir uma Atividade
     */
    const deleteActivity = useCallback(async (activityId) => {
        if (!idTenant) return;

        const activityToDelete = activities.find(a => a.id === activityId);

        const confirmed = await ConfirmDialog({
            title: "Excluir Atividade?",
            text: `Tem certeza que deseja excluir "${activityToDelete?.name || 'esta atividade'}"? Esta ação não pode ser desfeita.`,
            confirmButtonText: "Sim, excluir",
            cancelButtonText: "Cancelar"
        });

        if (!confirmed) return;

        try {
            await ActivityService.delete(activityId, idTenant, user, activityToDelete?.name);
            toast.success("Atividade removida!");
            refresh();
            return true;
        } catch (err) {
            console.error("Erro ao excluir atividade:", err);
            toast.error("Não foi possível excluir.");
            return false;
        }
    }, [idTenant, user, activities, refresh]);

    /**
     * Reordenar Atividades
     */
    const reorderActivities = useCallback(async (startIndex, endIndex) => {
        if (!idTenant) return;

        const items = Array.from(activities);
        const [reorderedItem] = items.splice(startIndex, 1);
        items.splice(endIndex, 0, reorderedItem);

        try {
            // Atualização otimista local (opcional, usePagination já reagiria se atualizássemos o estado do cache, 
            // mas aqui apenas chamamos o serviço e ele dará o refresh)
            await ActivityService.reorder(items, idTenant, user);
            refresh();
            return true;
        } catch (err) {
            console.error("Erro ao reordenar atividades:", err);
            toast.error("Erro ao salvar nova ordem.");
            return false;
        }
    }, [idTenant, user, activities, refresh]);

    /**
     * Seed manual das atividades padrão
     */
    const seedDefaults = useCallback(async () => {
        if (!idTenant) return;
        setIsSaving(true);

        try {
            const count = await ActivityService.seedDefaults(idTenant, user);
            if (count > 0) {
                toast.success(`${count} atividades padrão criadas!`);
                refresh();
            } else {
                toast.info("As atividades padrão já existem.");
            }
            return true;
        } catch (err) {
            console.error("Erro ao criar atividades padrão:", err);
            toast.error("Erro ao criar atividades padrão.");
            return false;
        } finally {
            setIsSaving(false);
        }
    }, [idTenant, user, refresh]);

    return {
        activities,
        loading,
        error,
        isSaving,
        hasNeoMethodology,
        planId,
        actions: {
            saveActivity,
            deleteActivity,
            reorderActivities,
            seedDefaults,
            refresh
        }
    };
};
